import React from 'react';
import NProgress from 'nprogress';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es';
import { InstantSearch } from 'react-instantsearch';
import { Card, formatCurrency, Input, Button } from '@avila-tek/ui/src';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { FormHeader } from '../shared/Forms';
import { Benefit, Company, Plan, ShopCart, ShopItem, User } from '../../models';
import { EmployeeList, ProductItem } from '../shared/AssignModal';
import {
  ADD_ITEM_TO_SHOP_CART,
  ASSING_BENEFIT_TO_EMP,
} from '../../graphql/mutation';
import {
  useAlgolia,
  useGoalsDashboard,
  useNotify,
  useShopCart,
  useUser,
} from '../../hooks';
import { GET_BENEFIT, GET_COMPANY_POINTS } from '../../graphql/queries';
import TotalItem from '../shared/AssignModal/TotalItem';

dayjs.extend(localizedFormat);
dayjs.locale('es');

interface AssignBenefitProps {
  benefit: Benefit;
  plans: Plan[];
}

function AssignBenefit({ benefit, plans }: AssignBenefitProps) {
  const router = useRouter();
  const notify = useNotify();
  const [user] = useUser();
  const [shopCart, setShopCart] = useShopCart();
  const { searchClient } = useAlgolia();
  const goalDashboard = useGoalsDashboard(user.company as Company, {});

  const [date, setDate] = React.useState('');
  const [shopList, setShopList] = React.useState<ShopItem[]>([]);
  const [disabled, setDisabled] = React.useState(false);

  const [addItemToShopCartMutation] = useMutation<{
    addItemToShopCart: ShopCart;
  }>(ADD_ITEM_TO_SHOP_CART);

  const [assignBenefitToEmp] = useMutation<{
    assignBenefitsEmployees: {
      _id: string;
      name: string;
      email: string;
      domain: string;
    };
  }>(ASSING_BENEFIT_TO_EMP);

  const { data: dataGetCompanyBenefitInfo } = useQuery<{
    benefit: Benefit;
  }>(GET_BENEFIT, {
    variables: {
      filter: {
        company: (user.company as Company)._id,
      },
    },
  });

  const { data: dataGetBenefit } = useQuery<{
    getCompanyTotalPoints: {
      totalPoints: number;
    };
  }>(GET_COMPANY_POINTS, {
    variables: {
      data: {
        companyId: (user?.company as Company)._id,
      },
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      if (disabled) return;
      NProgress.start();
      setDisabled(true);

      if (shopList.length <= 0) {
        setDisabled(false);
        NProgress.done();
        return notify('Debe seleccionar al menos un beneficiario', 'error');
      }

      if (date === '') {
        setDisabled(false);
        NProgress.done();
        return notify('Debe seleccionar una fecha', 'error');
      }

      const benefitsPerClient = shopList.map((c) => ({
        userId: (c.client as User)?._id,
        quantity: c.quantity,
      }));
      const totalPointsToAssign = benefitsPerClient.reduce(
        (sum, current) => sum + current.quantity,
        0
      );

      if (
        (dataGetBenefit.getCompanyTotalPoints.totalPoints ?? 0) /
          (dataGetCompanyBenefitInfo.benefit.value ?? 1) >=
        totalPointsToAssign
      ) {
        notify('Serán pagados los beneficios con puntos prepagados', 'info');
        setTimeout(async () => {
          const { data: dataAssingBenefitToEmp } = await assignBenefitToEmp({
            variables: {
              data: {
                companyId: (user.company as Company)._id,
                benefitEmployees: benefitsPerClient,
              },
            },
          });
          notify(
            dataAssingBenefitToEmp.assignBenefitsEmployees
              ? 'Han sido asignados los beneficios con éxito'
              : 'Ha ocurrido un error al asignar los beneficios',
            dataAssingBenefitToEmp.assignBenefitsEmployees ? 'success' : 'error'
          );
          goalDashboard.totalPoints.refetchCompayPoints();
          setDisabled(false);
          NProgress.done();
          router.push('/app/employees');
        }, 2000);
        return;
      }
      notify(
        'Por no tener puntos prepagados suficientes, se enviará la solicitud al carrito de compras',
        'info'
      );
      setTimeout(async () => {
        const { data } = await addItemToShopCartMutation({
          variables: {
            data: {
              shopCart: shopCart._id,
              items: {
                benefit: benefit._id,
                shopList: shopList.map((c) => ({
                  client: (c.client as User)?.client?._id,
                  quantity: c.quantity,
                  benefitPrice: benefit?.value,
                  availableDate: new Date(date),
                })),
              },
            },
          },
        });
        if (data?.addItemToShopCart?._id) {
          notify(
            'Han sido procesada la asignación de beneficio con éxito',
            'success'
          );
          setShopCart(data?.addItemToShopCart);
        } else {
          notify(
            'Ha ocurrido un error al procesar la asignación de beneficios',
            'error'
          );
        }
        setDisabled(false);
        NProgress.done();
        router.push('/app/shopcart');
      }, 2000);
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  const getTotal = () => {
    let total = 0;
    shopList.forEach((c) => {
      total += c.quantity * (benefit?.value ?? 0);
    });
    return total;
  };

  return (
    <form onSubmit={onSubmit}>
      <Card>
        <FormHeader title="Asignar beneficios" />
        <Card.Body>
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Input
                value={`$${formatCurrency(benefit?.value)}`}
                label="Importe"
                type="text"
                placeholder="Ingrese el importe del beneficio"
                required
                disabled
              />
            </div>
            <div className="w-full">
              <Input
                value={date}
                label="Fecha de entrega"
                type="date"
                placeholder="Seleccione la fecha de entrega"
                onChange={(e) => {
                  e.preventDefault();
                  setDate(e.target.value);
                }}
              />
            </div>
          </div>
          <InstantSearch indexName="users" searchClient={searchClient}>
            <EmployeeList
              shopList={shopList}
              setShopList={setShopList}
              type="benefit"
              plans={plans}
              benefitValue={benefit?.value}
            />
          </InstantSearch>
          <div className="w-full flex flex-col mt-5">
            {shopList.map(
              (c) =>
                c.quantity > 0 && (
                  <ProductItem
                    key={`${(c.client as User)?._id}-${c.quantity}`}
                    title={benefit.title}
                    subtitle={`${(c.client as User).firstName} ${
                      (c.client as User).lastName
                    }`}
                    quantity={c.quantity}
                    price={benefit?.value}
                  />
                )
            )}
          </div>
          <TotalItem total={getTotal()} />
        </Card.Body>
      </Card>
      <div className="flex justify-end mt-5">
        <Button className="w-48" type="submit">
          Agregar al carrito
        </Button>
      </div>
    </form>
  );
}

export default AssignBenefit;
